"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.processSkillsMutations = processSkillsMutations;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _rdfMutationHelpers = require("../shared/rdfMutationHelpers");
var _idNodeFactory = require("../shared/idNodeFactory");
var _texts = require("../../texts");
var _debug = require("../../utils/debug");
const ESCO_SKILL_BASE_URI = 'http://data.europa.eu/esco/skill/';
const SKILL_PREFIX_BASE_URI = 'skill:';
function ensureSkillPrefix(store) {
  (0, _rdfMutationHelpers.registerStorePrefix)(store, 'skill', SKILL_PREFIX_BASE_URI);
}
function buildSkillPublicIdNode(publicId) {
  const normalized = publicId.trim();
  if (!normalized) return (0, _rdflib.blankNode)();
  if (normalized.startsWith('_:')) {
    return (0, _rdflib.blankNode)(normalized.slice(2));
  }
  if (normalized.startsWith('skill:')) {
    return (0, _rdflib.sym)(normalized);
  }
  if (normalized.startsWith(ESCO_SKILL_BASE_URI)) {
    const suffix = normalized.slice(ESCO_SKILL_BASE_URI.length);
    return (0, _rdflib.sym)(suffix ? `skill:${suffix}` : normalized);
  }
  if (!normalized.includes(':')) {
    return (0, _rdflib.blankNode)(normalized);
  }
  return (0, _rdflib.sym)(normalized);
}
function normalizeNodeId(value) {
  return value.startsWith('_:') ? value.slice(2) : value;
}
function collectSkillLinkStatementsByEntryValue(store, subject, doc, entryNode) {
  const normalizedEntryNode = normalizeNodeId(entryNode);
  return store.statementsMatching(subject, _solidUi.ns.schema('skills'), null, doc).filter(statement => {
    const objectValue = normalizeNodeId(statement.object.value);
    return objectValue === normalizedEntryNode || statement.object.value === entryNode;
  });
}
function buildSkillsStatements(subject, doc, node, skill) {
  if (!skill.name) return [];
  const publicIdNode = buildSkillPublicIdNode(skill.publicId);
  return [(0, _rdflib.st)(subject, _solidUi.ns.schema('skills'), node, doc), (0, _rdflib.st)(node, _solidUi.ns.solid('publicId'), publicIdNode, doc), (0, _rdflib.st)(publicIdNode, _solidUi.ns.schema('name'), (0, _rdflib.literal)(skill.name), doc)];
}
async function mutateSkillsEntries(store, subject, skillOps) {
  ensureSkillPrefix(store);
  const doc = subject.doc();
  const existingSkillNodes = store.each(subject, _solidUi.ns.schema('skills'));
  const deletions = [];
  const insertions = [];
  const collectLinkedPublicIdStatements = skillNode => {
    const linkedPublicIdStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, skillNode, _solidUi.ns.solid('publicId'), doc);
    deletions.push(...linkedPublicIdStatements.linkedStatements);
  };
  skillOps.remove.forEach(skill => {
    if (!skill.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingSkillNodes, skill.entryNode);
    deletions.push(...collectSkillLinkStatementsByEntryValue(store, subject, doc, skill.entryNode));
    if (existingNode) {
      if (existingNode.termType !== 'Literal') {
        deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
        if (existingNode.termType === 'NamedNode') {
          collectLinkedPublicIdStatements(existingNode);
        }
      }
    }
  });
  skillOps.update.forEach(skill => {
    if (!skill.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingSkillNodes, skill.entryNode);
    if (!existingNode) {
      insertions.push(...buildSkillsStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), skill));
      return;
    }
    deletions.push(...collectSkillLinkStatementsByEntryValue(store, subject, doc, skill.entryNode));
    if (existingNode.termType !== 'Literal') {
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
      if (existingNode.termType === 'NamedNode') {
        collectLinkedPublicIdStatements(existingNode);
      }
    }
    if (existingNode.termType === 'NamedNode') {
      insertions.push(...buildSkillsStatements(subject, doc, existingNode, skill));
      return;
    }
    insertions.push(...buildSkillsStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), skill));
  });
  skillOps.create.forEach(skill => {
    insertions.push(...buildSkillsStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), skill));
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: 'Skill updates are not supported by this store updater.',
    failureMessage: 'Failed to save skills',
    useDavFallback: true,
    usePutFallback: true
  });
}
async function processSkillsMutations(store, subject, mutationPlan) {
  try {
    await mutateSkillsEntries(store, subject, mutationPlan);
  } catch (error) {
    const rootError = error instanceof Error ? error : new Error(String(error));
    (0, _debug.error)(_texts.skillsMutationSaveFailedDebugText, rootError);
    throw new Error(_texts.saveSkillsUpdatesFailedMessageText, {
      cause: rootError
    });
  }
}